import React from 'react';

import { useStates } from '@uplift/queries';
import { State } from '@uplift/types';

import { Autocomplete, AutocompleteMulti } from '../AutocompleteInput';
import { BaseTextFieldProps } from '../BaseTextField';

export type AutoCompleteProps = {
  id?: string;
  name: string;
  label?: string;
  className?: string;
  fullWidth?: boolean;
  inputClasses?: BaseTextFieldProps['classes'];
  availableStatesOnly?: boolean;
  testId?: string;
  multiple?: boolean;
} & Partial<Pick<React.ComponentProps<typeof Autocomplete>, 'onChange' | 'color'>>;

export const StatesAutocomplete: React.VFC<AutoCompleteProps> = ({ id, name, availableStatesOnly = false, testId = 'states-autocomplete', multiple = false, ...props }) => {
  const { states } = useStates();
  const filteredStates = availableStatesOnly ? states.filter(state => state?.available) : states;

  const autoFiller = (value: string) => {
    const state = states.find(o => o.fullName === value || o.shortName === value) ?? null;

    if (!state) {
      return null;
    }

    return { value: state, inputValue: state.fullName ?? '' };
  };

  if (multiple) {
    return (
      <AutocompleteMulti
        {...props}
        size="small"
        color={props.color as any}
        name={name}
        options={filteredStates}
        getOptionLabel={(opt: State) => opt?.fullName ?? ''}
        extractId
        data-testid={testId}
        disableCloseOnSelect
      />
    );
  }

  return (
    <Autocomplete
      {...props}
      id={id ?? name}
      name={name}
      options={filteredStates}
      getOptionLabel={(opt: State) => opt.fullName ?? ''}
      transformValue={v => v?.id}
      autoFiller={autoFiller}
      blurOnSelect
      getOptionKey={opt => opt.id}
      selectOnFocus={false}
      data-testid={testId}
    />
  );
};
